<template>
  <QueueTable
    :rows="rows"
    :columns="columns"
    :row-key="rowKey"
    :sort="sort"
    :selectable="selectable"
    :selected-ids="selectedIds"
    :is-row-selectable="isRowSelectable"
    :test-id="testId"
    @update:selected-ids="emit('update:selectedIds', $event)"
    @update:sort="emit('update:sort', $event)"
  >
    <template #cell-time="{ row }">
      <PrioritySlaCountdown
        :sla="row.sla ?? null"
        :row="row"
        :now-ms="nowMs"
        :thresholds="thresholdsOf(row)"
        :test-id="`priority-payments-countdown-${row.doctorStationId}`"
      />
    </template>
    <template #cell-state="{ row }">
      <PrioritySlaChip
        :sla="row.sla ?? null"
        :now-ms="nowMs"
        :thresholds="thresholdsOf(row)"
        :test-id="`priority-payments-chip-${row.doctorStationId}`"
      />
    </template>
    <template #cell-sla-kind="{ row }">
      <template v-if="row.sla">
        <span
          v-if="row.sla.state !== 'NOT_STARTED'"
          class="inline-block rounded border px-1.5 py-px text-[11px] font-bold"
          :class="
            row.sla.kind === 'ANTECIPE'
              ? 'border-blue-300 text-blue-700'
              : 'border-primary text-primary'
          "
        >
          {{ formatSlaKindLabel(row.sla, row, nowMs) }}
        </span>
        <span class="mt-1 block text-xs text-neutral50">
          {{ formatSlaStageLine(row.sla, row.situation) }}
        </span>
        <p
          v-if="alsoOf(row)"
          class="m-0 mt-1 text-xs text-neutral50"
          :data-testid="`priority-payments-also-${row.doctorStationId}`"
        >
          Também: <b class="text-textPrimary">{{ alsoOf(row)?.kindLabel }}</b> ·
          {{ alsoOf(row)?.detail }}
        </p>
      </template>
      <span v-else class="text-xs text-neutral50">—</span>
    </template>
    <template #cell-doctor="{ row }">
      <div class="font-medium">{{ row.doctorName }}</div>
      <div class="text-xs text-neutral50">{{ row.healthUnitName ?? row.scheduleName }}</div>
    </template>
    <template #cell-station="{ row }">
      <span class="whitespace-nowrap">
        {{ stationTimeOf(row).text }}
        <span
          class="ml-1 rounded px-1.5 py-0.5 font-mono text-[11px]"
          :class="
            stationTimeOf(row).isOutsideBrasilia
              ? 'bg-blue-50 text-blue-700'
              : 'bg-lightGray30 text-neutral50'
          "
          :data-testid="`priority-payments-timezone-${row.doctorStationId}`"
        >
          {{ stationTimeOf(row).timezoneLabel }}
        </span>
      </span>
      <div class="mt-1 flex flex-wrap gap-1">
        <span
          v-for="(type, index) in row.types"
          :key="`${type}-${index}`"
          class="rounded border border-current px-1.5 py-px text-[11px] font-bold"
          :class="
            type.startsWith('IN_CASH')
              ? 'text-primary'
              : type === 'ANTECIPE_PAID'
                ? 'text-red-700'
                : 'text-blue-700'
          "
        >
          {{ PRIORITY_TYPE_LABELS[type] }}
        </span>
      </div>
    </template>
    <template #cell-situation="{ row }">
      <span
        class="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
        :class="QUEUE_TONE_PILL_CLASS[PRIORITY_SITUATION_TONES[row.situation]]"
      >
        {{ prioritySituationLabel(row) }}
      </span>
      <div class="mt-1 flex flex-wrap gap-1">
        <span
          v-for="reason in priorityCriticalReasons(row)"
          :key="reason"
          class="rounded-full border border-red-200 bg-white px-2 py-0.5 text-[11px] font-medium text-red-700"
          :data-testid="`priority-payments-critical-${row.doctorStationId}`"
        >
          {{ reason }}
        </span>
      </div>
      <p
        v-if="row.activityRecord?.rejectionJustification"
        class="mt-1 max-w-[38ch] border-l-2 border-red-600 pl-2 text-xs text-neutral50"
        :data-testid="`priority-payments-justification-${row.doctorStationId}`"
      >
        {{ row.activityRecord.rejectionJustification }}
      </p>
    </template>
    <template #actions="{ row }">
      <button
        v-for="action in priorityRowActions(row)"
        :key="action.key"
        type="button"
        class="rounded-[5px] border border-lightGray30 bg-transparent px-2 py-0.5 text-xs text-primary"
        :data-testid="action.testId"
        @click="emit('action', { key: action.key, row })"
      >
        {{ action.label }}
      </button>
    </template>
  </QueueTable>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import QueueTable from '@/components/designSystem/QueueViews/QueueTable.vue'
import { QUEUE_TONE_PILL_CLASS } from '@/components/designSystem/QueueViews/types'
import type { QueueSort } from '@/components/designSystem/QueueViews/types'
import type {
  PriorityConfig,
  PriorityPaymentRow
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { priorityRowActions } from '../actions'
import type { PriorityActionDescriptor } from '../actions'
import {
  PRIORITY_SITUATION_TONES,
  PRIORITY_TYPE_LABELS,
  prioritySituationLabel
} from '../constants'
import { formatPriorityStationTime } from '../rowFormat'
import {
  formatSlaAlso,
  formatSlaKindLabel,
  formatSlaStageLine,
  priorityCriticalReasons,
  priorityThresholdsFor
} from '../slaFormat'
import { prioritySlaTableColumns } from '../tableColumns'
import PrioritySlaChip from './PrioritySlaChip.vue'
import PrioritySlaCountdown from './PrioritySlaCountdown.vue'

/**
 * Tabela do M3, usada na Tabela plana (com seleção e ordenação) e dentro de
 * cada grupo da Lista (sem elas). Um lugar só para as células com marcação.
 */
const props = withDefaults(
  defineProps<{
    rows: PriorityPaymentRow[]
    serverNow: string
    nowMs: number | null
    config: PriorityConfig | null
    selectable?: boolean
    selectedIds?: number[]
    isRowSelectable?: (row: PriorityPaymentRow) => boolean
    sort?: QueueSort | null
    testId?: string
  }>(),
  {
    selectable: false,
    selectedIds: () => [],
    isRowSelectable: undefined,
    sort: null,
    testId: 'priority-payments-table'
  }
)

const emit = defineEmits<{
  'update:selectedIds': [ids: number[]]
  'update:sort': [sort: QueueSort]
  action: [payload: { key: PriorityActionDescriptor['key']; row: PriorityPaymentRow }]
}>()

const columns = computed(() => prioritySlaTableColumns(props.serverNow))

const rowKey = (row: PriorityPaymentRow): number => row.doctorStationId

const thresholdsOf = (row: PriorityPaymentRow) =>
  row.sla ? priorityThresholdsFor(props.config, row.sla.kind) : null

const alsoOf = (row: PriorityPaymentRow) =>
  row.sla?.secondary ? formatSlaAlso(row.sla.secondary, row, props.nowMs, props.config) : null

const stationTimeOf = (row: PriorityPaymentRow) => formatPriorityStationTime(row)
</script>
